import React, { useState } from "react";
import { Layout } from "views/Main/Layouts";
import "./PolicyInheritance.scss";
import { ReactComponent as AnimatedLine } from "assets/images/policies-animated-line.svg";
import { ReactComponent as PolicyDot } from "assets/images/policy-rounded-rectangle.svg";
import { ReactComponent as SiteLine } from "assets/images/policies-site-line.svg";
import { ReactComponent as UserGroupLine } from "assets/images/policies-user-group-line.svg";
import { ReactComponent as GroupLine } from "assets/images/policies-group-and-model-line-short.svg";
import { ReactComponent as ModelLineLong } from "assets/images/policies-model-line.svg";
import { LocalKeys, useTranslation } from "utilities/i18n";
import { useProvisioningAccountsQuery } from "generated/graphql";
import { noOpNotifyContext } from "polygraphql";
import EnableAccountProvisioningModal from "./EnableAccountProvisioningModal";
import { useTenantId } from "polyrouter/SelectedTenantProvider";
import { Link } from "polyrouter";
import { generatePath, useRouteMatch } from "react-router";

const PolicyInheritance: React.FC = () => {
  const { Content } = Layout;
  const tenantId = useTenantId();
  const { t } = useTranslation();
  const [deviceHover, setDeviceHover] = useState(false);
  const [userGroupHover, setUserGroupHover] = useState(false);
  const [siteHover, setSiteHover] = useState(false);
  const [groupHover, setGroupHover] = useState(false);
  const [modelHover, setModelHover] = useState(false);
  const [hoverClasses, setHoverState] = useState(["animated-line"]);
  const { url } = useRouteMatch();

  const { data: provData, fetching } = useProvisioningAccountsQuery({
    variables: { tenantUuid: tenantId },
    context: noOpNotifyContext
  });
  const account = provData?.getProvisioningAccounts?.[0];
  const provDisabled = !account || !!account.isDisabled;

  const somethingFocused =
    userGroupHover || siteHover || groupHover || modelHover || deviceHover;
  const userGroupNotFocused =
    deviceHover || modelHover || siteHover || groupHover;
  const siteNotFocused =
    deviceHover || userGroupHover || groupHover || modelHover;
  const groupNotFocused =
    siteHover || deviceHover || userGroupHover || modelHover;
  const modelNotFocused =
    siteHover || groupHover || deviceHover || userGroupHover;

  const dotUserGroupNotFocused = deviceHover;
  const dotSiteNotFocused = deviceHover || userGroupHover;
  const dotGroupNotFocused = siteHover || deviceHover || userGroupHover;
  const dotModelNotFocused =
    siteHover || groupHover || deviceHover || userGroupHover;

  const { inheritance, overrides } = getHoverBoxText(
    deviceHover,
    userGroupHover,
    siteHover,
    groupHover,
    modelHover
  );

  const ModelLine: React.FunctionComponent<React.SVGProps<
    SVGSVGElement
  >> = ModelLineLong;
  const animatedLineLeft = userGroupHover
    ? "415px"
    : siteHover
    ? "425px"
    : groupHover
    ? "455px"
    : modelHover
    ? "465px"
    : "";

  return (
    <Content className="policy policy-inheritance" scrolling>
      <Content.Title>{t("INHERITANCE_AND_PRIORITY")}</Content.Title>
      <p className="policy-subheader mt-5 mb-2">{t("POLICIES_SUBHEADER")}</p>
      <h5>{t("PRIORITY")}</h5>
      <div
        className="policy-inheritance policy-buttons"
        data-testid="policy-inheritance-group"
      >
        <AnimatedLine
          className={hoverClasses.join(" ")}
          style={{ left: animatedLineLeft }}
        />
        <UserGroupLine
          className={`policy-line ${userGroupNotFocused ? "not-focused" : ""}`}
        />
        <SiteLine
          className={`policy-line ${siteNotFocused ? "not-focused" : ""}`}
        />
        <GroupLine
          className={`group policy-line ${
            groupNotFocused ? "not-focused" : ""
          }`}
        />
        <ModelLine
          className={`model policy-line ${
            modelNotFocused ? "not-focused" : ""
          }`}
        />
        <div
          className={[
            "hover-box",
            somethingFocused && "visible",
            deviceHover && "device",
            userGroupHover && "user-group",
            groupHover && "group",
            siteHover && "site",
            modelHover && "model"
          ]
            .filter(c => !!c)
            .join(" ")}
          data-testid="hoverBox"
        >
          <h6>{t("INHERITANCE")}</h6>
          <p>{t(inheritance)}</p>
          <h6>{t("OVERRIDES")}</h6>
          <p>{t(overrides)}</p>
        </div>
        <ol className="policy-list">
          <li
            onMouseEnter={() => {
              setDeviceHover(true);
              setHoverState([...hoverClasses, "device", "animate"]);
            }}
            onMouseLeave={() => {
              setDeviceHover(false);
              setHoverState(["animated-line"]);
            }}
          >
            <Link to={generatePath(url + "/list/:scope", { scope: "device" })}>
              <PolicyButton header>
                {t("POLICY_DEVICE")}
                <PolicyDot
                  className="policy-dot"
                  data-testid="device-policy-dot"
                />
                <PolicyDot
                  className={`policy-dot user-group ${
                    dotUserGroupNotFocused ? "not-focused" : ""
                  }`}
                  data-testid="user-group-policy-dot"
                />
                <PolicyDot
                  className={`policy-dot site ${
                    dotSiteNotFocused ? "not-focused" : ""
                  }`}
                  data-testid="site-policy-dot"
                />
                <PolicyDot
                  className={`policy-dot group ${
                    dotGroupNotFocused ? "not-focused" : ""
                  }`}
                  data-testid="group-policy-dot"
                />
                <PolicyDot
                  className={`policy-dot model ${
                    dotModelNotFocused ? "not-focused" : ""
                  }`}
                  style={{ left: "423px" }}
                  data-testid="model-policy-dot"
                />
              </PolicyButton>
            </Link>
          </li>
          <li
            className={userGroupNotFocused ? "not-focused" : ""}
            onMouseEnter={() => {
              setUserGroupHover(true);
              setHoverState([...hoverClasses, "user-group", "animate"]);
            }}
            onMouseLeave={() => {
              setUserGroupHover(false);
              setHoverState(["animated-line"]);
            }}
          >
            <Link
              to={generatePath(url + "/list/:scope", { scope: "user_group" })}
            >
              <PolicyButton
                className="user-group"
                data-testid="user-group-policy-button"
              >
                {t("DEVICE_USER_GROUP")}
              </PolicyButton>
            </Link>
          </li>
          <li
            className={siteNotFocused ? "not-focused" : ""}
            onMouseEnter={() => {
              setSiteHover(true);
              setHoverState([...hoverClasses, "site", "animate"]);
            }}
            onMouseLeave={() => {
              setSiteHover(false);
              setHoverState(["animated-line"]);
            }}
          >
            <Link to={generatePath(url + "/list/:scope", { scope: "site" })}>
              <PolicyButton className="site" data-testid="site-policy-button">
                {t("SITE")}
              </PolicyButton>
            </Link>
          </li>
          <li
            className={groupNotFocused ? "not-focused" : ""}
            onMouseEnter={() => {
              setGroupHover(true);
              setHoverState([...hoverClasses, "group", "animate"]);
            }}
            onMouseLeave={() => {
              setGroupHover(false);
              setHoverState(["animated-line"]);
            }}
          >
            <Link to={generatePath(url + "/list/:scope", { scope: "group" })}>
              <PolicyButton className="group" data-testid="group-policy-button">
                {t("POLICY_GROUP")}
              </PolicyButton>
            </Link>
          </li>
          <li
            className={modelNotFocused ? "not-focused" : ""}
            onMouseEnter={() => {
              setModelHover(true);
              setHoverState([...hoverClasses, "model", "animate"]);
            }}
            onMouseLeave={() => {
              setModelHover(false);
              setHoverState(["animated-line"]);
            }}
          >
            <Link to={generatePath(url + "/list/:scope", { scope: "model" })}>
              <PolicyButton className="model" data-testid="model-policy-button">
                {t("DEVICE_MODEL")}
              </PolicyButton>
            </Link>
          </li>
        </ol>
      </div>
      {provDisabled && !fetching && (
        <EnableAccountProvisioningModal tenant={tenantId ?? ""} />
      )}
    </Content>
  );
};

const getHoverBoxText = (
  device: boolean,
  user_group: boolean,
  site: boolean,
  group: boolean,
  model: boolean
): { inheritance: LocalKeys; overrides: LocalKeys } => {
  if (user_group) {
    return {
      inheritance: "POLICY_INHERITANCE_USER_GROUP",
      overrides: "POLICY_OVERRIDES_USER_GROUP"
    };
  }
  if (site) {
    return {
      inheritance: "POLICY_INHERITANCE_SITE",
      overrides: "POLICY_OVERRIDES_DEFAULT"
    };
  }
  if (group) {
    return {
      inheritance: "POLICY_INHERITANCE_GROUP",
      overrides: "POLICY_OVERRIDES_DEFAULT"
    };
  }
  if (model) {
    return {
      inheritance: "POLICY_INHERITANCE_MODEL",
      overrides: "POLICY_OVERRIDES_DEFAULT"
    };
  } else {
    return {
      inheritance: "POLICY_INHERITANCE_DEVICE",
      overrides: "POLICY_OVERRIDES_DEVICE"
    };
  }
};

const PolicyButton: React.FC<{
  className?: string;
  header?: boolean;
  "data-testid"?: string;
}> = ({ children, header, className, ...rest }) => {
  return (
    <div
      className={`policy-button ${className} ${
        header ? "policy-header-button" : ""
      }`}
      data-testid={rest["data-testid"] ?? "policy-button"}
    >
      <b>{children}</b>
    </div>
  );
};

export default PolicyInheritance;
